import { Banner, Box, Divider, Stack, Text } from '@stitch-fix/mode-react';
import React, { useState, useEffect } from 'react';
import { D } from '@mobily/ts-belt';
import type { AxiosResponse } from 'axios';
import { FacebookButton, type OnFacebookErrorParams } from './FacebookButton';
import { GoogleButton, type OnGoogleErrorParams } from './GoogleButton';
import { AppleButton, type OnAppleErrorParams } from './AppleButton';
import type {
  RegistrationResponseCreated,
  RegistrationResponseExisting,
  RegistrationSignupType,
} from '../../utils/requests/clientAuth/registration';
import {
  loginByFacebookToken,
  loginByGoogleToken,
  type LoginAuthResponse,
} from '../../utils/requests/clientAuth/login';
import { isAndroidApp } from '../../utils/registration/android';

declare global {
  interface StitchfixGlobal {
    AndroidAPI?: {
      handleFacebookLogin: (
        path: string,
        action: string,
        fbAccessToken: string,
      ) => void;
      handleGoogleLogin: (token: string) => void;
    };
  }
}

export type CapitalizedBusinessLine = 'Kids' | 'Mens' | 'Womens';

export const getFormattedBusinessLine = (
  businessLine: unknown,
): CapitalizedBusinessLine | undefined => {
  let formattedBusinessLine;

  if (Array.isArray(businessLine)) {
    formattedBusinessLine = businessLine[0];
  } else {
    formattedBusinessLine = businessLine;
  }

  if (typeof formattedBusinessLine === 'string') {
    switch (formattedBusinessLine?.toLowerCase()) {
      case 'mens':
        formattedBusinessLine = 'Mens';
        break;
      case 'womens':
        formattedBusinessLine = 'Womens';
        break;
      case 'kids':
        formattedBusinessLine = 'Kids';
        break;
      default:
        return undefined;
    }
  }

  return formattedBusinessLine;
};

export type SocialButtonErrorParams =
  | { type: 'Apple'; data: OnAppleErrorParams }
  | { type: 'Facebook'; data: OnFacebookErrorParams }
  | { type: 'Google'; data: OnGoogleErrorParams };

export type SocialButtonsProps = (
  | {
      mode: 'signup';
      onRegistration: (params: RegistrationResponseCreated) => void;
      businessLine: CapitalizedBusinessLine;
      zipCode?: string;
      registrationSignupType?: RegistrationSignupType;
      onLogin: (
        params: RegistrationResponseExisting | LoginAuthResponse,
      ) => void;
      isWafEnabled?: boolean;
      inline?: boolean;
    }
  | {
      mode: 'login';
      onLogin: (params: LoginAuthResponse) => void;
      isWafEnabled?: boolean;
      inline?: boolean;
    }
) & {
  onError: (params: SocialButtonErrorParams) => void;
};

export const SocialButtons = (props: SocialButtonsProps) => {
  const [error, setError] = useState<string | null>();
  const errorBanner = error && (
    <Banner variant="error-border" onClose={() => setError(null)}>
      {error}
    </Banner>
  );

  const handleError = (params: SocialButtonErrorParams) => {
    setError(
      `Invalid username or password for your ${params.type} account. Try again, or create an account.`,
    );
    props.onError(params);
  };

  useEffect(() => {
    if (props.mode === 'signup') {
      return;
    }

    const handleResponse = async (
      type: SocialButtonErrorParams['type'],
      loginAuthPromise: Promise<
        AxiosResponse<LoginAuthResponse, SocialButtonErrorParams>
      >,
    ) => {
      loginAuthPromise
        .then(({ data }) => props.onLogin(data))
        .catch(loginError => props.onError({ type, data: loginError }));
    };

    // a public API for the Android app to call.
    // This acts as a seam to allow internal JS to be refactored
    // while still providing the same functionality to the app.
    // Adapted from: https://github.com/stitchfix/hellblazer/blob/8b241951c24a13783cf80d750865b8327ffc14a8/app/assets/javascripts/android.js#L3-L8
    window.stitchfix = {
      ...window?.stitchfix,
      AndroidAPI: {
        ...window?.stitchfix?.AndroidAPI,

        // handleFacebookLogin & handleGoogleLogin are called by the Android app
        // with the respective social token that the app retrieved natively.
        // After we login given the token, we get back either a resolved or rejected
        // promise. `handleResponse` will either redirect to a URL on success
        // or display an error message on failure.
        handleFacebookLogin: (
          // path & action come from the Android app.
          // we don't need them
          path,
          action,
          fbAccessToken,
        ) => {
          handleResponse(
            'Facebook',
            loginByFacebookToken(fbAccessToken, props.isWafEnabled),
          );
        },
        handleGoogleLogin: token => {
          handleResponse(
            'Google',
            loginByGoogleToken(token, props.isWafEnabled),
          );
        },
      },
    };
  });

  const renderSocialButtons = () => {
    const buttons = {
      Facebook: FacebookButton,
      Google: GoogleButton,
      Apple: AppleButton,
    };

    return D.keys(buttons).map(type => {
      const SocialButton = buttons[type];

      return (
        <SocialButton
          key={type}
          // @ts-ignore the 'type' will match the key of the buttons object
          onError={data => handleError({ type, data })}
          {...(props.mode === 'signup'
            ? {
                mode: 'signup',
                onRegistration: props.onRegistration,
                businessLine: props.businessLine,
                zipCode: props.zipCode,
                registrationSignupType: props.registrationSignupType,
                onLogin: props.onLogin,
                isWafEnabled: props.isWafEnabled,
                inline: props.inline,
              }
            : {
                mode: 'login',
                onLogin: props.onLogin,
                isWafEnabled: props.isWafEnabled,
                inline: props.inline,
              })}
        />
      );
    });
  };

  if (props.mode === 'signup' && isAndroidApp()) {
    return null;
  }

  if (props.inline) {
    return (
      <>
        <Text setting="subtitle-small" color="blue-45">
          Or continue with
        </Text>

        <Box mt={1}>{errorBanner}</Box>

        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '20px',
            maxWidth: '342px',
          }}
        >
          {renderSocialButtons().reverse()}
        </Box>
      </>
    );
  }

  return (
    <>
      <Divider variant="section" my={2}>
        <Text setting="eyebrow-small" as="span">
          or
        </Text>
      </Divider>

      <Stack spacing={1} hAlign="stretch">
        {errorBanner}
        {renderSocialButtons()}
      </Stack>
    </>
  );
};
